import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Layout from 'antd/lib/layout';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import { TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import EditOutlined from '@ant-design/icons/lib/icons/EditOutlined';
import ReloadOutlined from '@ant-design/icons/lib/icons/ReloadOutlined';
import { AbilityContext } from 'src/components/ability/can';
import { CustomizableTableContextProvider } from 'src/components/table/CustomizableTableContext';
import { CustomizableColumnType, CustomizableTable } from 'src/components/table/CustomizableTable';
import { ActionList, IAction } from 'src/components/table/actionList/actionList';
import { PageFooter } from 'src/components/PageFooter';
import { PageHeader } from '../../components/PageHeader';
import { SearchInput } from 'src/components/searchInput/searchInput';
import { WithEmptyDataTable } from 'src/components/table/withEmptyDataTable';
import { useDocumentTitle } from 'src/components/useDocumentTitle';
import { SidebarGrid } from 'src/components/Sidebar/SidebarGrid';
import { AsyncJobsModal } from 'src/components/salesforce/AsyncJobsModal/AsyncJobsModal';
import { BillStatusTag } from 'src/components/customer-utility-bill/BillStatusTag';
import { EMPTY_VALUE } from 'src/components/customer-utility-bill/billStyles';
import { FileIdCell } from 'src/components/customer-utility-bill/FileIdCell';
import { BillFilesReparseModal } from 'src/components/customer-utility-bill/files/BillFilesReparseModal';
import { EditBillFileModal } from 'src/components/customer-utility-bill/files/edit-file/EditBillFileModal';
import { IPaginationMeta } from 'src/domain/IPagination';
import { DEFAULT_PAGINATION } from 'src/domain/commonConst';
import { getComputerFormattedTime } from 'src/domain/date/date';
import { JobName } from 'src/domain/async-job/interface';
import { ICustomerUtilityBillFilesQuery } from 'src/domain/customer-utility-bill-file';
import { ICustomerUtilityBillFile, billSourceLabel, formatCustomerName } from 'src/domain/customer-utility-bill-file/interface';
import { useCustomerUtilityBillFilesQuery, useReparseCustomerUtilityBillFilesMutation } from 'src/domain/customer-utility-bill-file/queries';
import { usePageLocation } from '../usePageState';
import { CustomerUtilityBillViewSwitcher } from './customerUtilityBillViewInterface';
import { BillFilesSidebar } from './billFilesSidebar/BillFilesSidebar';
import { useBillFilesSidebar } from './billFilesSidebar/useBillFilesSidebar';
import { IBillFilesPageState } from './billFilesSidebar/BillFilesSidebarFiltersContext';
import './customerUtilityBill.css';

const { Text } = Typography;

type TSorter = { field?: string; order?: 'ascend' | 'descend' };

// Mirrors the backend default ordering (updatedAt desc) so the column shows an active sort arrow up front.
const DEFAULT_SORTER: TSorter = { field: 'updatedAt', order: 'descend' };

const toArr = (v: unknown): string[] => (v == null ? [] : Array.isArray(v) ? v.map(String) : [String(v)]);

// Pluralizes the file count for batch notifications ("1 file" / "3 files").
const pluralFiles = (count: number) => `${count} ${count === 1 ? 'file' : 'files'}`;

const dash = <Text type="secondary">{EMPTY_VALUE}</Text>;

const dateCell = (v: string | null) => (v ? <Text>{getComputerFormattedTime(v)}</Text> : dash);

export const CustomerUtilityBillFiles = () => {
    useDocumentTitle('Customer Utility Bill Files');

    const { notification } = App.useApp();
    const location = useLocation();
    const { setPageQuery, queryToState } = usePageLocation();

    // Sync statuses (async jobs) are admin-only — same gate the Salesforce sync buttons use elsewhere.
    const ability = useContext(AbilityContext);
    const canViewSyncJobs = ability.can('create', 'AsyncJob');

    const [editingRow, setEditingRow] = useState<ICustomerUtilityBillFile | null>(null);
    const [jobsModalOpen, setJobsModalOpen] = useState(false);
    const [reparseModalOpen, setReparseModalOpen] = useState(false);
    const { isFiltersSidebarEnabled, sidebarCollapsed, setSidebarCollapsed, layoutPaddingLeft } = useBillFilesSidebar();

    // ─── Page state (URL) ────────────────────────────────────────────────
    // Filters are written into the URL by BillFilesSidebarFiltersContext; the page only reads them to build the query.
    // `queryToState` is a pure parser of the URL string, so memoizing on the raw search is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pageState = useMemo(() => queryToState(location.search) as IBillFilesPageState, [location.search]);

    const filters = useMemo(
        () => ({
            sources: toArr(pageState.filter?.sources),
            customers: toArr(pageState.filter?.customers),
            statuses: toArr(pageState.filter?.statuses),
        }),
        [pageState]
    );

    const search = pageState.search ?? '';

    const pagination = useMemo(
        () => ({
            current: pageState.pagination?.current ?? 1,
            pageSize: pageState.pagination?.pageSize ?? DEFAULT_PAGINATION.pageSize,
        }),
        [pageState]
    );

    const sorter = useMemo<TSorter>(() => pageState.sorter ?? DEFAULT_SORTER, [pageState]);

    const query = useMemo<ICustomerUtilityBillFilesQuery>(
        () => ({
            ...(search && { search }),
            ...(filters.sources.length && { sources: filters.sources.join(',') }),
            ...(filters.customers.length && { customers: filters.customers.join(',') }),
            ...(filters.statuses.length && { statuses: filters.statuses.join(',') }),
            ...(sorter.field && sorter.order && { sortField: sorter.field, sortOrder: sorter.order }),
            offset: (pagination.current - 1) * pagination.pageSize,
            limit: pagination.pageSize,
        }),
        [search, filters, sorter, pagination]
    );

    const { data, isFetching, isLoading } = useCustomerUtilityBillFilesQuery(query);
    const meta: IPaginationMeta | undefined = data?.meta;
    const rows = data?.data ?? [];

    // usePageLocation resets pagination to page 1 automatically when search changes
    const handleSearch = (value: string) => setPageQuery({ ...pageState, search: value });

    const handleTableChange = (
        tablePagination: TablePaginationConfig,
        _filter: Record<string, FilterValue | null>,
        tableSorter: SorterResult<ICustomerUtilityBillFile> | SorterResult<ICustomerUtilityBillFile>[]
    ) => {
        const nextSorter = Array.isArray(tableSorter) ? tableSorter[0] : tableSorter;
        const rawField = (nextSorter?.field ?? nextSorter?.columnKey) as string | undefined;
        // antd blanks field/columnKey when a controlled sort is cancelled — fall back to the current field.
        const cancelledField = sorter.field ?? DEFAULT_SORTER.field;
        const newSorter: TSorter = rawField
            ? { field: rawField, ...(nextSorter?.order && { order: nextSorter.order as 'ascend' | 'descend' }) }
            : { field: cancelledField };

        setPageQuery({
            ...pageState,
            pagination: { current: tablePagination.current ?? 1, pageSize: tablePagination.pageSize ?? DEFAULT_PAGINATION.pageSize },
            sorter: newSorter,
        });
    };

    // ─── Selection + batch re-parse ──────────────────────────────────────
    // Full selected records (not just keys) so the reparse modal can show rows selected on other pages.
    const [selectedRows, setSelectedRows] = useState<ICustomerUtilityBillFile[]>([]);
    const selectedRowKeys = useMemo(() => selectedRows.map(row => row.id), [selectedRows]);

    const handleSelectionChange = useCallback((keys: React.Key[], changedRows: ICustomerUtilityBillFile[]) => {
        setSelectedRows(prev => {
            const byId = new Map<React.Key, ICustomerUtilityBillFile>(prev.map(row => [row.id, row]));
            changedRows.forEach(row => row && byId.set(row.id, row));
            return keys.map(key => byId.get(key)).filter((row): row is ICustomerUtilityBillFile => !!row);
        });
    }, []);

    const reparseMutation = useReparseCustomerUtilityBillFilesMutation();

    // Resolves with the accepted ids (for live tracking) — [] on a no-op or failure (errors are already notified).
    const handleReparseSelected = (): Promise<string[]> => {
        if (!selectedRowKeys.length) return Promise.resolve([]);
        return reparseMutation
            .mutateAsync(selectedRowKeys)
            .then(res => {
                if (res.reparsedIds.length)
                    notification.info({
                        message: `Re-parse requested for ${pluralFiles(res.reparsedIds.length)}`,
                        description: 'Existing bills from these files will be deleted and re-created once parsing finishes.',
                    });
                if (res.skipped.length) notification.warning({ message: `Skipped ${pluralFiles(res.skipped.length)} that couldn’t be re-parsed` });
                setSelectedRows([]);
                return res.reparsedIds;
            })
            .catch(() => []);
    };

    // Single-file re-parse from the file modal. Resolves with whether the file was actually queued, so the modal
    // knows whether to start polling for the outcome (false on a skip or failure; errors are already notified).
    const handleReparseFile = (row: ICustomerUtilityBillFile): Promise<boolean> =>
        reparseMutation
            .mutateAsync([row.id])
            .then(res => {
                const queued = res.reparsedIds.length > 0;
                if (queued) {
                    notification.info({
                        message: 'Re-parse requested for the file',
                        description: 'Existing bills from this file will be deleted and re-created once parsing finishes.',
                    });
                } else {
                    const reason = res.skipped[0]?.reason;
                    notification.warning({ message: reason ? `Couldn’t re-parse the file: ${reason}` : 'Couldn’t re-parse the file' });
                }
                return queued;
            })
            .catch(() => false);

    // ─── Columns ─────────────────────────────────────────────────────────
    // Only processingStatus / downloadedAt / updatedAt are sortable — the backend sort allowlist doesn't cover
    // the included source / bills relations.
    const columns = useMemo<CustomizableColumnType<ICustomerUtilityBillFile>[]>(() => {
        const fileColumns: CustomizableColumnType<ICustomerUtilityBillFile>[] = [
            { title: 'File ID', dataIndex: 'id', key: 'fileId', width: 140, render: v => <FileIdCell fileId={v} /> },
            {
                title: 'Source',
                key: 'source',
                width: 130,
                render: (_, r) => (r.source?.source ? billSourceLabel(r.source.source) : dash),
            },
            {
                title: 'Customer Name',
                key: 'customerName',
                width: 200,
                render: (_, r) => (r.source?.customerName ? <Text>{formatCustomerName(r.source.customerName)}</Text> : dash),
            },
            {
                title: 'Status',
                dataIndex: 'processingStatus',
                key: 'processingStatus',
                width: 190,
                render: (_, r) =>
                    r.processingError ? (
                        <Tooltip title={r.processingError}>
                            <span>
                                <BillStatusTag status={r.processingStatus} />
                            </span>
                        </Tooltip>
                    ) : (
                        <BillStatusTag status={r.processingStatus} />
                    ),
                sorter: true,
            },
            {
                title: 'Number of created Bills',
                key: 'numberOfBills',
                width: 140,
                align: 'right',
                render: (_, r) => <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{r.bills?.length ?? 0}</Text>,
            },
            { title: 'Downloaded At', dataIndex: 'downloadedAt', key: 'downloadedAt', width: 170, render: dateCell, sorter: true },
            { title: 'Updated At', dataIndex: 'updatedAt', key: 'updatedAt', width: 170, render: dateCell, sorter: true },
            {
                title: 'Actions',
                key: 'actions',
                width: 60,
                hideable: false,
                render: (_, r) => {
                    const actions: IAction[] = [
                        { title: 'Edit', icon: <EditOutlined />, onClick: (row: ICustomerUtilityBillFile) => () => setEditingRow(row) },
                    ];
                    return <ActionList actions={actions} item={r} alwaysShow />;
                },
            },
        ];

        // Reflect the active URL sort on the column header (sorting is server-side / controlled order).
        return fileColumns.map(col => (col.sorter ? { ...col, sortOrder: sorter.field === String(col.key) ? (sorter.order ?? null) : null } : col));
    }, [sorter]);

    return (
        <CustomizableTableContextProvider configKey="customerBillFiles" columns={columns}>
            <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                {isFiltersSidebarEnabled && (
                    <BillFilesSidebar
                        collapsed={sidebarCollapsed}
                        onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                        totalRecords={meta?.total}
                        onFilterChange={() => {}}
                        gridComponent={<SidebarGrid />}
                    />
                )}

                <Layout.Content style={{ padding: '0 26px' }}>
                    <PageHeader
                        pageTitle="Customer Utility Bill Files"
                        extra={<CustomerUtilityBillViewSwitcher />}
                        actions={[
                            <SearchInput key="search" defaultValue={search} onSearch={handleSearch} />,
                            selectedRowKeys.length > 0 && (
                                <Button
                                    key="reparse-selected"
                                    size="large"
                                    loading={reparseMutation.isLoading}
                                    icon={<ReloadOutlined />}
                                    onClick={() => setReparseModalOpen(true)}
                                >
                                    Re-parse Selected ({selectedRowKeys.length})
                                </Button>
                            ),
                            selectedRowKeys.length > 0 && (
                                <Button key="clear-selection-action" size="large" onClick={() => setSelectedRows([])}>
                                    Clear Selected ({selectedRowKeys.length})
                                </Button>
                            ),
                            canViewSyncJobs && (
                                <Button key="sync" size="large" type="primary" onClick={() => setJobsModalOpen(true)}>
                                    Sync Customer Utility Bill Files
                                </Button>
                            ),
                        ]}
                    />

                    <WithEmptyDataTable condition={isLoading || isFetching}>
                        <CustomizableTable<ICustomerUtilityBillFile>
                            rowKey="id"
                            size="small"
                            className="sites-table"
                            showHiddenColumnsCount
                            sticky={{ offsetHeader: 62 }}
                            loading={isLoading || isFetching}
                            dataSource={rows}
                            meta={meta as IPaginationMeta}
                            onChange={handleTableChange}
                            pagination={{ current: pagination.current, pageSize: pagination.pageSize }}
                            rowSelection={{
                                selectedRowKeys,
                                onChange: handleSelectionChange,
                                preserveSelectedRowKeys: true,
                                columnWidth: 40,
                            }}
                        />
                    </WithEmptyDataTable>

                    {editingRow && <EditBillFileModal row={editingRow} onReparse={handleReparseFile} onClose={() => setEditingRow(null)} />}
                    {jobsModalOpen && canViewSyncJobs && (
                        <AsyncJobsModal isModalOpen jobName={JobName.CUSTOMER_UTILITY_BILLS_IMPORT} onClose={() => setJobsModalOpen(false)} />
                    )}
                    {reparseModalOpen && (
                        <BillFilesReparseModal rows={selectedRows} onConfirm={handleReparseSelected} onClose={() => setReparseModalOpen(false)} />
                    )}

                    <PageFooter />
                </Layout.Content>
            </Layout>
        </CustomizableTableContextProvider>
    );
};
